import type { WindowId } from "./places.ts";

/**
 * Which play window is in front (P2.8-T16; ADR-061's amendment "a window can be moved", item 8: "when a second
 * window that is not modal exists, a press on it raises its layer in the router and its drawing order together").
 * The page keeps one of these for its play windows. A window is raised as it opens and by a press anywhere on it; the
 * window raised last is drawn over the others (`front`), and each window's keys hear the change and make its router
 * layer the topmost, so Escape closes the window the player last touched. No second order is kept beside the
 * router's: this names only the front window, and a window that is closed while in front leaves the others as they
 * stand until one is raised.
 *
 * @example
 * ```ts
 * const order = new WindowOrder();
 * order.raise("chest");
 * order.front("chest");
 * // true
 * ```
 */
export class WindowOrder {
  private top: WindowId | null = null;
  private readonly listeners = new Set<() => void>();

  /**
   * Listens for a change of the front window.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The window in front.
   *
   * @returns Its id, or null before any window was raised.
   */
  public readonly getSnapshot = (): WindowId | null => this.top;

  /**
   * Whether a window is the one in front.
   *
   * @param id The window.
   * @returns True when it was raised last.
   */
  public front(id: WindowId): boolean {
    return this.top === id;
  }

  /**
   * Brings a window to the front; a window already there changes nothing and tells nobody.
   *
   * @param id The window.
   */
  public raise(id: WindowId): void {
    if (this.top === id) {
      return;
    }
    this.top = id;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
